import { useMemo, useState } from 'react';
import { useBuilders, useJobs } from '../lib/queries';
import { StageCard } from './StageCard';
import { WeatherHoldDialog } from './WeatherHoldDialog';
import { EmptyState, Select } from '../components/ui';
import { cn } from '../lib/utils';
import type { JobWithContext, JobStageWithStage } from '../lib/database.types';

type Bucket = 'today' | 'tomorrow' | 'week' | 'later' | 'overdue';

interface StageEntry {
    job: JobWithContext;
    js: JobStageWithStage;
    bucket: Bucket;
}

function bucketFor(dateISO: string | null): Bucket | null {
    if (!dateISO) return null;
    const d = new Date(`${dateISO}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'overdue';
    if (diff === 0) return 'today';
    if (diff === 1) return 'tomorrow';
    if (diff <= 7) return 'week';
    return 'later';
}

const BUCKET_TITLES: Record<Bucket, string> = {
    overdue: 'Overdue',
    today: 'Today',
    tomorrow: 'Tomorrow',
    week: 'This week',
    later: 'Later',
};

const BUCKET_ORDER: Bucket[] = ['overdue', 'today', 'tomorrow', 'week', 'later'];

export function FieldDashboard() {
    const { data: jobs, isLoading, error } = useJobs();
    const { data: builders = [] } = useBuilders();
    const [weatherHold, setWeatherHold] = useState<{ jobId: string; stageId: string | null; jobLabel: string } | null>(null);
    const [activeFilter, setActiveFilter] = useState<'all' | 'ppc'>('ppc');
    const [builderFilter, setBuilderFilter] = useState<string>('all');

    // Build the flat list of (job, jobStage) entries sorted by scheduled_date,
    // bucketed by proximity to today.
    const entriesByBucket = useMemo(() => {
        const buckets: Record<Bucket, StageEntry[]> = {
            overdue: [],
            today: [],
            tomorrow: [],
            week: [],
            later: [],
        };

        for (const job of jobs ?? []) {
            // Builder filter applies to the whole job
            if (builderFilter !== 'all' && job.builder_id !== builderFilter) continue;
            for (const js of job.stages) {
                // Skip complete stages and stages without a date
                if (js.status === 'complete') continue;
                if (!js.scheduled_date) continue;
                const b = bucketFor(js.scheduled_date);
                if (!b) continue;
                // Filter mode: PPC stages only shows stages PPC executes
                if (activeFilter === 'ppc' && !js.stage.ppc_controlled) continue;
                buckets[b].push({ job, js, bucket: b });
            }
        }

        // Sort each bucket by scheduled date ascending, then by job name
        for (const key of BUCKET_ORDER) {
            buckets[key].sort((a, b) => {
                const cmp = (a.js.scheduled_date ?? '').localeCompare(b.js.scheduled_date ?? '');
                if (cmp !== 0) return cmp;
                return a.job.community.name.localeCompare(b.job.community.name);
            });
        }
        return buckets;
    }, [jobs, activeFilter, builderFilter]);

    const totalEntries = BUCKET_ORDER.reduce((sum, k) => sum + entriesByBucket[k].length, 0);

    return (
        <div className="flex flex-col">
            {/* Filter bar */}
            <div className="sticky top-14 z-20 bg-graphite-50 dark:bg-graphite-950 border-b border-graphite-200 dark:border-graphite-800 px-4 py-2">
                <div className="flex items-center gap-2">
                    {(['ppc', 'all'] as const).map((mode) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => setActiveFilter(mode)}
                            className={cn(
                                'font-mono text-xs uppercase tracking-widest px-3 py-1.5 rounded transition-colors',
                                activeFilter === mode
                                    ? 'bg-graphite-200 text-graphite-900 dark:bg-graphite-800 dark:text-graphite-50'
                                    : 'text-graphite-600 hover:text-graphite-900 dark:text-graphite-500 dark:hover:text-graphite-200'
                            )}
                        >
                            {mode === 'ppc' ? 'PPC stages' : 'All stages'}
                        </button>
                    ))}
                    <div className="w-44 ml-1">
                        <Select
                            aria-label="Builder filter"
                            value={builderFilter}
                            onChange={(e) => setBuilderFilter(e.target.value)}
                        >
                            <option value="all">All builders</option>
                            {builders.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className="flex-1" />
                    <div className="font-mono text-xs text-graphite-500 tabular">
                        {totalEntries} scheduled
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-6">
                {error ? (
                    <div className="border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                        {(error as Error).message}
                    </div>
                ) : null}

                {isLoading ? (
                    <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                        Loading...
                    </div>
                ) : totalEntries === 0 ? (
                    <EmptyState
                        title="Nothing scheduled"
                        description="When Andrea schedules a stage, it will show up here."
                    />
                ) : (
                    BUCKET_ORDER.map((bucket) => {
                        const entries = entriesByBucket[bucket];
                        if (entries.length === 0) return null;
                        return (
                            <section key={bucket}>
                                <div className="flex items-baseline gap-3 mb-3 px-1">
                                    <h2
                                        className={cn(
                                            'font-display font-semibold uppercase tracking-wider text-sm',
                                            bucket === 'overdue' && 'text-amber-600 dark:text-amber-400',
                                            bucket === 'today' && 'text-signal-600 dark:text-signal-400',
                                            bucket === 'tomorrow' && 'text-graphite-900 dark:text-graphite-100',
                                            (bucket === 'week' || bucket === 'later') && 'text-graphite-700 dark:text-graphite-300'
                                        )}
                                    >
                                        {BUCKET_TITLES[bucket]}
                                    </h2>
                                    <span className="font-mono text-xs text-graphite-500 tabular">
                                        {entries.length}
                                    </span>
                                    <span className="flex-1 h-px bg-graphite-200 dark:bg-graphite-800" />
                                </div>
                                <div className="space-y-2">
                                    {entries.map((entry) => (
                                        <StageCard
                                            key={entry.js.id}
                                            job={entry.job}
                                            js={entry.js}
                                            onOpenWeatherHold={() =>
                                                setWeatherHold({
                                                    jobId: entry.job.id,
                                                    stageId: entry.js.stage_id,
                                                    jobLabel: `${entry.job.community.name} · Lot ${entry.job.lot_number}`,
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })
                )}
            </div>

            {weatherHold ? (
                <WeatherHoldDialog
                    open
                    onClose={() => setWeatherHold(null)}
                    jobId={weatherHold.jobId}
                    stageId={weatherHold.stageId}
                    jobLabel={weatherHold.jobLabel}
                />
            ) : null}
        </div>
    );
}
